import React, { useState, useEffect, useCallback } from 'react'
import { Globe2 } from 'lucide-react'
import {
  LineChart, Line,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { fetchAnalytics } from '../../../../api/analyticsClient'
import AnalyticsCard from '../components/AnalyticsCard'
import styles from './LanguageSection.module.css'

const COLORS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2', '#be185d', '#65a30d', '#7e22ce', '#b45309', '#0f766e', '#9f1239']

const LANG_NAMES = {
  hi: 'Hindi', bn: 'Bengali', gu: 'Gujarati', kn: 'Kannada',
  ml: 'Malayalam', mr: 'Marathi', or: 'Odia', pa: 'Punjabi',
  ta: 'Tamil', te: 'Telugu', en: 'English', not_set: 'Not Set',
}

const fmt = (n) => {
  if (n === null || n === undefined) return '—'
  const num = Number(n)
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
  return num.toLocaleString('en-IN')
}
const fmtPct = (n) => (n !== null && n !== undefined) ? parseFloat(n).toFixed(1) + '%' : '—'
const langName = (code) => LANG_NAMES[code] || code

const initState = () => ({ data: null, loading: true, error: null })

const LanguageSection = ({ dateRange }) => {
  const [distribution, setDistribution] = useState(initState)
  const [trend, setTrend] = useState(initState)
  const [byGeo, setByGeo] = useState(initState)
  const [byPlatform, setByPlatform] = useState(initState)
  const [geoLevel, setGeoLevel] = useState('state')
  const [trendGranularity, setTrendGranularity] = useState('monthly')

  const { from, to } = dateRange

  const loadAll = useCallback(async () => {
    setDistribution(s => ({ ...s, loading: true, error: null }))
    setTrend(s => ({ ...s, loading: true, error: null }))
    setByGeo(s => ({ ...s, loading: true, error: null }))
    setByPlatform(s => ({ ...s, loading: true, error: null }))

    const [distRes, trendRes, geoRes, platRes] = await Promise.all([
      fetchAnalytics('/analytics/language/distribution', { from, to }),
      fetchAnalytics('/analytics/language/trend', { from, to, granularity: trendGranularity }),
      fetchAnalytics('/analytics/language/by-geo', { from, to, level: geoLevel }),
      fetchAnalytics('/analytics/language/by-platform', { from, to }),
    ])

    setDistribution({ data: distRes.data, loading: false, error: distRes.error })
    setTrend({ data: trendRes.data, loading: false, error: trendRes.error })
    setByGeo({ data: geoRes.data, loading: false, error: geoRes.error })
    setByPlatform({ data: platRes.data, loading: false, error: platRes.error })
  }, [from, to, geoLevel, trendGranularity])

  useEffect(() => { loadAll() }, [loadAll])

  const handleTrendGranularity = async (g) => {
    setTrendGranularity(g)
    setTrend(s => ({ ...s, loading: true, error: null }))
    const res = await fetchAnalytics('/analytics/language/trend', { from, to, granularity: g })
    setTrend({ data: res.data, loading: false, error: res.error })
  }

  const handleGeoLevel = async (level) => {
    setGeoLevel(level)
    setByGeo(s => ({ ...s, loading: true, error: null }))
    const res = await fetchAnalytics('/analytics/language/by-geo', { from, to, level })
    setByGeo({ data: res.data, loading: false, error: res.error })
  }

  const totalUsers = distribution.data?.reduce((s, d) => s + (d.unique_users || 0), 0) || 0

  /* Build multi-line trend data: pivot from [{period, language, unique_users}] → [{period, hi: 100, bn: 50, ...}] */
  const trendLines = []
  const trendPivot = {}
  const langSet = new Set()
  if (trend.data?.length > 0) {
    trend.data.forEach(row => {
      if (!trendPivot[row.period]) trendPivot[row.period] = { period: row.period }
      trendPivot[row.period][row.language] = row.unique_users
      langSet.add(row.language)
    })
    langSet.forEach(l => trendLines.push(l))
  }
  const trendData = Object.values(trendPivot).sort((a, b) => a.period.localeCompare(b.period))

  /* Build platform pivot for grouped bar */
  const platformPivot = {}
  const platLangSet = new Set()
  if (byPlatform.data?.length > 0) {
    byPlatform.data.forEach(row => {
      if (!platformPivot[row.language]) platformPivot[row.language] = { language: langName(row.language) }
      platformPivot[row.language][row.platform] = row.unique_users
      platLangSet.add(row.platform)
    })
  }
  const platformData = Object.values(platformPivot)
  const platformKeys = Array.from(platLangSet)

  return (
    <div className={styles.section}>
      {/* Language Distribution */}
      <AnalyticsCard
        title="Language Distribution"
        subtitle="Unique users per language preference — key investor signal"
        loading={distribution.loading}
        error={distribution.error}
      >
        {distribution.data?.length > 0 && (
          <div className={styles.langList}>
            {distribution.data.map((item, i) => {
              const pct = totalUsers > 0 ? (item.unique_users / totalUsers) * 100 : item.user_pct || 0
              const isNotSet = item.language === 'not_set'
              return (
                <div key={item.language} className={styles.langRow}>
                  <div className={styles.langMeta}>
                    <span className={styles.langName}>{langName(item.language)}</span>
                    <span className={styles.langCode}>{isNotSet ? 'preference not set' : item.language}</span>
                  </div>
                  <div className={styles.langBarWrap}>
                    <div className={styles.langBarTrack}>
                      <div
                        className={styles.langBarFill}
                        style={{
                          width: `${Math.min(100, pct)}%`,
                          background: isNotSet ? '#94a3b8' : COLORS[i % COLORS.length],
                        }}
                      />
                    </div>
                  </div>
                  <span className={styles.langUsers}>{fmt(item.unique_users)}</span>
                  <span className={styles.langPct}>{fmtPct(item.user_pct || pct)}</span>
                </div>
              )
            })}
          </div>
        )}
      </AnalyticsCard>

      {/* Language Trend */}
      <AnalyticsCard
        title="Language Trend"
        subtitle="Which languages are growing — shows adoption over time"
        loading={false}
        error={trend.error}
        actions={
          <div className={styles.toggle}>
            {['weekly', 'monthly'].map(g => (
              <button
                key={g}
                className={`${styles.toggleBtn} ${trendGranularity === g ? styles.toggleBtnActive : ''}`}
                onClick={() => handleTrendGranularity(g)}
              >
                {g[0].toUpperCase() + g.slice(1)}
              </button>
            ))}
          </div>
        }
      >
        {trend.loading ? (
          <div className={styles.chartSkeleton} />
        ) : trendData.length === 0 ? (
          <div className={styles.empty}>No trend data for this period</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={fmt} />
              <Tooltip
                contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8 }}
                formatter={(v, name) => [fmt(v), langName(name)]}
              />
              <Legend
                formatter={langName}
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 11 }}
              />
              {trendLines.map((lang, i) => (
                <Line
                  key={lang}
                  type="monotone"
                  dataKey={lang}
                  stroke={lang === 'not_set' ? '#94a3b8' : COLORS[i % COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3 }}
                  strokeDasharray={lang === 'not_set' ? '4 2' : undefined}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </AnalyticsCard>

      {/* By Geo + By Platform */}
      <div className={styles.twoGrid}>
        <AnalyticsCard
          title="Language by Geography"
          subtitle="Dominant language per location"
          loading={byGeo.loading}
          error={byGeo.error}
          actions={
            <div className={styles.toggle}>
              {['city', 'state'].map(level => (
                <button
                  key={level}
                  className={`${styles.toggleBtn} ${geoLevel === level ? styles.toggleBtnActive : ''}`}
                  onClick={() => handleGeoLevel(level)}
                >
                  {level[0].toUpperCase() + level.slice(1)}
                </button>
              ))}
            </div>
          }
        >
          {byGeo.data?.top_language_by_location?.length > 0 && (
            <div className={styles.geoTableWrap}>
              <table className={styles.geoTable}>
                <thead>
                  <tr>
                    <th className={styles.geoTh}>Location</th>
                    <th className={styles.geoTh}>Top Language</th>
                    <th className={styles.geoTh}>Users</th>
                  </tr>
                </thead>
                <tbody>
                  {byGeo.data.top_language_by_location.map((row, i) => (
                    <tr key={i} className={styles.geoTr}>
                      <td className={styles.geoTd}>{row.location}</td>
                      <td className={styles.geoTd}>
                        <span className={styles.langBadge}>{langName(row.language)}</span>
                      </td>
                      <td className={styles.geoTd}>{fmt(row.unique_users)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title="Language by Platform"
          subtitle="Language preferences across Android and iOS"
          loading={byPlatform.loading}
          error={byPlatform.error}
        >
          {platformData.length > 0 && (
            <ResponsiveContainer width="100%" height={Math.min(360, platformData.length * 32 + 60)}>
              <BarChart data={platformData} layout="vertical" margin={{ top: 0, right: 16, left: 90, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={fmt} />
                <YAxis type="category" dataKey="language" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={90} />
                <Tooltip
                  contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8 }}
                  formatter={v => [fmt(v), 'Unique Users']}
                />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                {platformKeys.map((plat, i) => (
                  <Bar key={plat} dataKey={plat} stackId="a" fill={COLORS[i % COLORS.length]} radius={i === platformKeys.length - 1 ? [0, 4, 4, 0] : undefined} maxBarSize={16} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </AnalyticsCard>
      </div>
    </div>
  )
}

export default LanguageSection
